import { Component, ComponentFactoryResolver, ComponentRef, ViewChild, ViewContainerRef, HostListener, OnInit, AfterViewInit } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { ControllerSpecificationService } from "./services/controller-specification.service";
import { ComponentService, FullscreenService, MenuService, PaintService } from "components";
import { breakpoints } from "components";
declare let gtag: Function;

@Component({
  selector: 'ssvmn',
  templateUrl: './ssvmn.component.html',
  styleUrls: ['./ssvmn.component.css']
})

/**
 * Component that dynamically loads child components and manages menus, breadcrumbs, and navigation tracking.
 */
export class SsvmnComponent implements OnInit, AfterViewInit {

  @ViewChild('target', { read: ViewContainerRef }) target: ViewContainerRef;

  //constants
  public homeView : string = 'AC_VI99';

  public menuOptions : Map<string,string> = new Map<string,string>();
  public breadcrumbsData : string[] = [];
  private currentParams : string[] = [];
  private componentToLoad: string;
  private componentRef: ComponentRef<any>;
  public isMediumSize : boolean = false;
  public isSmallSize : boolean = false;
  public isLargeSize : boolean = false;
  public isFullScreen : boolean = false;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.manageWindowResizing(window.innerWidth);
  }

  constructor(private activatedRoute: ActivatedRoute, 
              private controllerSpecificationService: ControllerSpecificationService,
              private comp: ComponentFactoryResolver,
              private componentService: ComponentService, 
              private router: Router, 
              private paintService: PaintService,
              private menuService : MenuService,
              private fullScreenService : FullscreenService) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        gtag('config', 'G-KD7P5Q6XT3',
          {
            'page_path': event.urlAfterRedirects
          }
        );
      }
    })
  }

  ngAfterViewInit(): void {
    this.fullScreenService.showFullscreen$.subscribe(value => {
      this.isFullScreen = value;
    });
  }

  /**
   * This function manages the window resizing by updating the component's properties based 
   * on the provided width and predefined breakpoints.
   */
  private manageWindowResizing(width : number): void {
    this.isMediumSize = width < breakpoints.L && width >= breakpoints.M;
    this.isSmallSize = width >= breakpoints.S && width < breakpoints.M;
    this.isLargeSize = width >= breakpoints.L;
  }

  /**
   * This function is called when the component is initialized. It sets up a subscription to route parameters, loads the component, and gets the menu data.
   */
  public ngOnInit(): void {
    this.onResize();
    this.activatedRoute.params.subscribe((params: Params) => {
      this.paintService.vViewComponents = this.controllerSpecificationService.getViews();
      this.componentToLoad = this.findLastParam(params);
      this.loadComponent();
      this.addCurrentParams(params);
    });
    this.getMenu();
  }

  /**
   * This function loads the component based on the componentToLoad variable.
   */
  private loadComponent(): void {
    if (this.componentToLoad) {
      setTimeout(() => {
        this.changeView();
      });
    }
  }

  /**
   * This function iterates through route parameters to find the last one.
   * @param params - Object containing route parameters
   * @returns The value of the last route parameter
   */
  private findLastParam(params: Params): string {
    let lastParam = null;
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        lastParam = params[key];
      }
    }
    return lastParam;
  }

  /**
   * This function changes the view by clearing the target view container and loading the appropriate component.
   */
  private changeView(): void {
    let component: any;
    component = this.controllerSpecificationService.getView(this.componentToLoad);
    this.target.clear();

    if (component === undefined) component = this.componentService.getComponent(this.componentToLoad);

    if (component !== undefined) {
      const componentFactory = this.comp.resolveComponentFactory(component);
      this.componentRef = this.target.createComponent(componentFactory);
      this.componentRef.changeDetectorRef.detectChanges();
    } 
  }

  /**
   * This function fetches the menu data and saves it to the menuOptions map, then adds the breadcrumbs data.
   */
  private getMenu(): void {
    this.menuService.getMenuOptions().subscribe({
      next : (menuData : any) => {
        this.saveMenuData(menuData);
        this.addBreadcrumbsData();
      }
    });
  }

  /**
   * This function iterates through the menu data and saves it to the menuOptions map.
   * @param menuData - Object containing menu data
   */
  private saveMenuData(menuData : any): void {
    for(let data of menuData){
      if(data.childs.length === 0) {
        this.menuOptions.set(data.navigateTo,data.text);
      }
      else this.saveMenuData(data.childs);
    }
  }

  /**
   * This function adds the current route parameters to the currentParams array.
   * @param params - Object containing route parameters
   */
  private addCurrentParams(params : Params): void {
    for(let param in params){
      this.currentParams.push(params[param]);
    }
  }

  /**
   * This function adds the breadcrumbs data to the breadcrumbsData array based on the current route parameters.
   */
  private addBreadcrumbsData(): void {
    for(let param of this.currentParams){
      this.breadcrumbsData.push(this.menuOptions.get(param));
    }
    this.breadcrumbsData.pop();
  }

  /**
   *This function loads a view based on the selected breadcrumb item.
   *@param viewCode - The view code of the selected breadcrumb item
   */
  public loadViewFromBreadcrumb(viewCode : string): void {
    const newView : string = this.getCodeFromView(viewCode);
    const newUrl : string = this.getNewUrl(newView);
    this.router.navigateByUrl(newUrl);
  }

  /**
   * This function finds the corresponding view code from the given view name.
   * @param viewCode - The view name to find the corresponding view code
   * @returns The corresponding view code
   */
  private getCodeFromView(viewCode : string): string {
    let finalKey : string;
    for (let [key, value] of this.menuOptions.entries()) {
      if (value === viewCode) finalKey = key;
    }
    return finalKey;
  }

  /**
   * This function creates a new URL based on the given view code.
   * @param viewCode - The view code to generate a new URL
   * @returns The new URL
  */
  private getNewUrl(viewCode : string): string {
    let urlParts : string[] = this.router.url.split('/');

    for(let i=urlParts.length-1; i>=0;i--){
      if(urlParts[i] === viewCode) break;
      else if(urlParts[i] !== viewCode) urlParts.pop();
    }

    return urlParts.join('/');
  }

}